import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SOURCE, fmtDuration, resultTone } from "./jobMeta";
import { When } from "./When";

const BAR = { ok: "bg-emerald-400", failed: "bg-rose-500", interrupted: "bg-amber-400" };

function Bars({ runs, max }) {
  return (
    <div className="flex h-10 items-end gap-[3px]" data-testid="run-history-bars">
      {[...runs].reverse().map((r, i) => (
        <div key={i} title={`${r.status} · ${fmtDuration(r.duration_ms)}`} className={`w-2.5 rounded-sm ${BAR[r.status] || "bg-slate-300"}`}
             style={{ height: `${Math.max(12, max ? ((r.duration_ms ?? max) / max) * 100 : 100)}%` }} />))}
    </div>
  );
}

function Summary({ d }) {
  const s = d.summary;
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-slate-100 bg-slate-50/70 px-4 py-3 text-xs text-slate-600" data-testid="run-history-summary">
      <span><b className="text-slate-800">{s.count}</b> runs kept (newest {d.keep})</span>
      <span className={s.failed ? "text-rose-700" : ""}><b>{s.failed}</b> failed</span>
      <span>Average <b className="text-slate-800">{fmtDuration(s.avg_ms)}</b></span>
      <span>Longest <b className="text-slate-800">{fmtDuration(s.max_ms)}</b></span>
      {d.fail_streak > 0 && <span className="rounded bg-rose-100 px-1.5 py-0.5 font-semibold text-rose-800" data-testid="run-history-streak">
        Failing {d.fail_streak}× in a row{d.alert_open ? " · alert sent" : ""}</span>}
      <div className="ml-auto">{s.count > 0 && <Bars runs={d.runs} max={s.max_ms} />}</div>
    </div>
  );
}

/** The newest 20 runs of one job: when, how long, how it was started and what it reported. */
export function RunHistoryDialog({ job, onClose }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    setD(null);
    api.get(`/cron/jobs/${encodeURIComponent(job.key)}/runs`).then(({ data }) => setD(data)).catch(() => setD(false));
  }, [job.key]);
  useEffect(() => { load(); }, [load]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="run-history-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2">Run history — {job.label}
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={load} data-testid="run-history-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button></DialogTitle>
          <DialogDescription className="font-mono text-[11px]">{job.key}</DialogDescription></DialogHeader>
        {d === null && <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-slate-300" />}
        {d === false && <p className="py-6 text-center text-sm text-slate-500" data-testid="run-history-error">Could not load the history.</p>}
        {d && <div className="space-y-3">
          <Summary d={d} />
          {!d.runs.length ? <p className="py-8 text-center text-sm text-slate-500" data-testid="run-history-empty">No runs recorded yet — history starts with the next run.</p> : (
            <div className="max-h-[55vh] overflow-auto rounded-lg border border-slate-100">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white"><tr className="text-left text-[10px] uppercase tracking-wide text-slate-400">
                  <th className="px-3 py-2 font-semibold">Started</th><th className="px-3 py-2 font-semibold">Took</th>
                  <th className="px-3 py-2 font-semibold">How</th><th className="px-3 py-2 font-semibold">Result</th></tr></thead>
                <tbody>{d.runs.map((r, i) => {
                  const [tone, text] = r.status === "interrupted" ? ["text-amber-700", r.result] : resultTone(r.result);
                  return (
                    <tr key={i} className="border-t border-slate-50 align-top" data-testid={`run-history-row-${i}`}>
                      <td className="whitespace-nowrap px-3 py-2"><When iso={r.started_at} testid={`run-history-start-${i}`} /></td>
                      <td className="whitespace-nowrap px-3 py-2 font-mono" data-testid={`run-history-took-${i}`}>{fmtDuration(r.duration_ms)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-500">{SOURCE[r.source] || r.source || "—"}</td>
                      <td className={`px-3 py-2 break-words ${tone}`} data-testid={`run-history-result-${i}`}>{text}</td>
                    </tr>);
                })}</tbody>
              </table>
            </div>)}
        </div>}
      </DialogContent>
    </Dialog>
  );
}
